import React, { useState } from "react";
import Requester from "../helpers/Requester";
import { useBinanceState } from "../contexts/BinanceContext";


type GridParams = {
    stepBuy: number;
    stepSell: number;
    quantityPerStep: number;
    gridSize: number;
    condition: "buy" | "sell" | null;
};

type GridProfitCalculatorProps = {
    symbol: string;
};

export const GridProfitCalculator: React.FC<GridProfitCalculatorProps> = ({ symbol }) => {
    const { prices, balances } = useBinanceState();

    const [params, setParams] = useState<GridParams>({
        stepBuy: 0.03,
        stepSell: 0.04,
        quantityPerStep: 0.25,
        gridSize: 3,
        condition: null,
    });

    const currentPrice = parseFloat(prices[symbol]);

    const baseAsset = symbol.replace("FDUSD", ""); // SOL
    const quoteAsset = "FDUSD";

    const balanceBase = parseFloat(balances[baseAsset]?.available ?? "0");   // SOL
    const balanceQuote = parseFloat(balances[quoteAsset]?.available ?? "0"); // FDUSD

    const { stepBuy, stepSell, quantityPerStep, gridSize, condition } = params;

    const startGrid = () => {
        const requester = new Requester();

        requester.post(requester.getUrl('api/grid'), {
            'symbol': 'SOLFDUSD',
            'step_buy': stepBuy,
            'step_sell': stepSell,
            'grid_size': gridSize,
            'quantity_per_step': quantityPerStep,
            'condition': condition == 'buy' ? 'buy' : condition == 'sell' ? 'sell' : null,
        }).then((response) => {
            console.log(response);

            if (response.errorMessage) {
                alert("Ошибка: " + response.errorMessage);
                return;
            }
        }).catch((error) => {
            alert("Ошибка: " + error.message);
            console.error("Ошибка при запуске сеточной торговли:", error);
        });
    }

    const handleChange = (field: keyof GridParams, value: number | string | null) => {
        setParams(prev => ({ ...prev, [field]: value }));
    };


    // уровни покупок
    const buyLevels = Array.from({ length: gridSize }).map((_, i) => {
        return currentPrice - (i + 1) * stepBuy;
    });

    // уровни продаж
    const sellLevels = Array.from({ length: gridSize }).map((_, i) => {
        return currentPrice + (i + 1) * stepSell;
    });

    // прибыль за каждый цикл
    const cycles = buyLevels.map((buyPrice, i) => {
        const sellPrice = sellLevels[i];
        const spread = sellPrice - buyPrice;
        const profit = spread * quantityPerStep;

        return {
            i: i + 1,
            buyPrice,
            sellPrice,
            spread,
            profit,
        };
    });

    // общая прибыль
    const totalProfit = cycles.reduce((acc, c) => acc + c.profit, 0);

    const requiredFDUSD = buyLevels.reduce(
        (sum, buyPrice) => sum + buyPrice * quantityPerStep,
        0
    );

    const requiredSOL = quantityPerStep * gridSize;

    const hasEnoughFDUSD = balanceQuote >= requiredFDUSD;
    const hasEnoughSOL = balanceBase >= requiredSOL;


    const canStartGrid = condition === 'buy' ? hasEnoughFDUSD : condition === 'sell' ? hasEnoughSOL : hasEnoughFDUSD && hasEnoughSOL;

    return (
        <div className="bg-white shadow p-6 rounded-xl max-w-2xl mx-auto space-y-6">

            <h2 className="text-2xl font-bold">Калькулятор сеточной прибыли</h2>

            {/* Параметры */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="font-medium">Текущая цена</label>
                    <input
                        type="number"
                        value={currentPrice}
                        step="0.01"
                        readOnly
                        className="w-full border rounded px-2 py-1"
                    />
                </div>

                <div>
                    <label className="font-medium">Условная сетка?</label>
                    <select
                        onChange={e => handleChange("condition", e.target.value === "" ? null : (e.target.value as "buy" | "sell"))}
                        className="w-full border rounded px-2 py-1"
                    >
                        <option value={""} selected={condition === null}>Нет</option>
                        <option value={"buy"} selected={condition === "buy"}>Создать SELL только после BUY</option>
                        <option value={"sell"} selected={condition === "sell"}>Создать BUY только после SELL</option>
                    </select>
                </div>

                <div>
                    <label className="font-medium">Step Buy</label>
                    <input
                        type="number"
                        value={stepBuy}
                        step="0.01"
                        onChange={e => handleChange("stepBuy", parseFloat(e.target.value))}
                        className="w-full border rounded px-2 py-1"
                    />
                </div>

                <div>
                    <label className="font-medium">Step Sell</label>
                    <input
                        type="number"
                        value={stepSell}
                        step="0.01"
                        onChange={e => handleChange("stepSell", parseFloat(e.target.value))}
                        className="w-full border rounded px-2 py-1"
                    />
                </div>

                <div>
                    <label className="font-medium">Количество SOL</label>
                    <input
                        type="number"
                        value={quantityPerStep}
                        step="0.01"
                        onChange={e => handleChange("quantityPerStep", parseFloat(e.target.value))}
                        className="w-full border rounded px-2 py-1"
                    />
                </div>

                <div>
                    <label className="font-medium">Размер сетки</label>
                    <input
                        type="number"
                        value={gridSize}
                        min={1}
                        onChange={e => handleChange("gridSize", parseInt(e.target.value))}
                        className="w-full border rounded px-2 py-1"
                    />
                </div>
            </div>

            {/* Таблица уровней */}
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="border-b bg-gray-50">
                            <th className="py-2 px-2 text-left">#</th>
                            <th className="py-2 px-2 text-left">Buy</th>
                            <th className="py-2 px-2 text-left">Sell</th>
                            <th className="py-2 px-2 text-left">Spread</th>
                            <th className="py-2 px-2 text-left">Profit (USDT)</th>
                        </tr>
                    </thead>

                    <tbody>
                        {cycles.map(c => (
                            <tr key={c.i} className="border-b">
                                <td className="py-2 px-2">{c.i}</td>
                                <td className="py-2 px-2">{c.buyPrice.toFixed(4)}</td>
                                <td className="py-2 px-2">{c.sellPrice.toFixed(4)}</td>
                                <td className="py-2 px-2">{c.spread.toFixed(4)}</td>
                                <td className="py-2 px-2 font-bold text-green-600">{c.profit.toFixed(4)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Балансы */}
            <div className="p-4 bg-gray-50 rounded-lg border">
                <div>Баланс SOL: <b>{balanceBase}</b></div>
                <div>Баланс FDUSD: <b>{balanceQuote}</b></div>
            </div>

            {/* Требования */}
            <div className="p-4 bg-gray-50 rounded-lg border">
                <div className={hasEnoughSOL ? "text-green-600" : "text-red-600"}>
                    Требуется SOL для SELL: <b>{requiredSOL.toFixed(4)}</b>
                </div>

                <div className={hasEnoughFDUSD ? "text-green-600" : "text-red-600"}>
                    Требуется FDUSD для BUY: <b>{requiredFDUSD.toFixed(4)}</b>
                </div>
            </div>

            {/* Итог */}
            <div className="text-xl font-bold">
                Общая прибыль: <span className="text-green-600">{totalProfit.toFixed(4)} USDT</span>
            </div>

            <button
                onClick={startGrid}
                disabled={!canStartGrid}
                className={`${canStartGrid ? "bg-indigo-600 hover:bg-indigo-700" : "bg-gray-400 cursor-not-allowed"} text-white px-4 py-2 rounded-md`}
            >
                {canStartGrid ? "Запустить сетку" : "Недостаточно баланса"}
            </button>
        </div>
    );
};